import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService, LocalStorageService } from 'src/app/core/core.module';
import { FundsPortalService } from 'src/app/core/services/funds-portal.service';
import { AtramModalComponent } from 'src/app/shared/components/atram-modal/atram-modal.component';

@Component({
  selector: 'app-email-otp-verify',
  templateUrl: './email-otp-verify.component.html',
  styleUrls: ['./email-otp-verify.component.less']
})
export class EmailOtpVerifyComponent implements OnInit {

  @ViewChild('atramModal')
  public atramModal: AtramModalComponent = new AtramModalComponent;

  public searchFormGroup!: FormGroup;
  public isError: boolean = false;
  public isInvalidCode: boolean = false;
  public isNotValidInputs: boolean = false;
  public invalidText: string = '';
  public isLockedOut: boolean = false;
  public lockoutCountDownTracker: string = '';
  private lockoutCountDown: number = 0;

  private readonly genericChallengeErrorText = 'Invalid setup challenge. Please try again or request a new one.';

  constructor(
    public fundsPortalService: FundsPortalService,
    public authService: AuthService,
    public router: Router,
    public localStorageService: LocalStorageService
  ) {}

  ngOnInit(): void {
    if (this.localStorageService.get('enroll_context') !== 'login') {
      this.router.navigateByUrl('', { skipLocationChange: false });
      return;
    }
    this.initializeFormGroup();

    const challengeError = this.localStorageService.get('setup_challenge_error');
    if (challengeError) {
      this.isInvalidCode = true;
      this.isError = true;
      this.invalidText = this.genericChallengeErrorText;
      this.localStorageService.remove('setup_challenge_error');
    }

    const lockoutSecondsStr = this.localStorageService.get('setup_lockout_seconds');
    if (lockoutSecondsStr) {
      this.isLockedOut = true;
      this.lockoutCountDown = Number(lockoutSecondsStr);
      this.localStorageService.remove('setup_lockout_seconds');
      this.runLockoutTimer();
    }
  }

  initializeFormGroup(): void {
    this.searchFormGroup = new FormGroup({
      temporaryPassword: new FormControl('', [Validators.required])
    });
  }

  runLockoutTimer(): void {
    if (this.lockoutCountDown > 0) {
      const mins = Math.floor(this.lockoutCountDown / 60);
      const secs = this.lockoutCountDown % 60;
      this.lockoutCountDownTracker = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
      this.lockoutCountDown--;
      setTimeout(() => this.runLockoutTimer(), 1000);
    } else {
      this.isLockedOut = false;
      this.lockoutCountDownTracker = '';
    }
  }

  proceed(): void {
    if (this.isLockedOut) return;
    if (!this.searchFormGroup.get('temporaryPassword')?.value?.trim()) {
      this.isError = true;
      this.isNotValidInputs = true;
      return;
    }
    this.isError = false;
    this.isInvalidCode = false;
    this.invalidText = '';
    this.isNotValidInputs = false;
    this.submit();
  }

  submit(): void {
    const tempPassword = this.searchFormGroup.get('temporaryPassword')?.value?.trim();
    this.ensureSessionThenProceed(tempPassword);
  }

  private ensureSessionThenProceed(tempPassword: string): void {
    const hasValidBearer =
      this.localStorageService.get('bearer') !== null &&
      !this.localStorageService.isExpired('bearer');

    if (hasValidBearer) {
      const expiry = Number(this.localStorageService.get('bearer_expiry_actual') || '600000') / 1000;
      this.localStorageService.set('temporary_password', tempPassword, expiry);
      this.router.navigateByUrl('/sign-up/totp-enroll', { skipLocationChange: false });
      return;
    }

    const email = this.localStorageService.get('email');
    const companyCode = this.localStorageService.get('login_company_code');
    const password = this.localStorageService.get('plainPass');

    if (!email || !companyCode || !password) {
      this.isInvalidCode = true;
      this.isError = true;
      this.invalidText = 'Session expired. Please open the invite link again to continue.';
      return;
    }

    const loginBody = {
      email: email,
      company_code: companyCode,
      password: password
    };

    this.authService.postLogin(loginBody).subscribe({
      next: (loginData: any) => {
        const token = loginData?.response_data?.token;
        if (!token?.access_token) {
          this.isInvalidCode = true;
          this.isError = true;
          this.invalidText = 'Session expired. Please open the invite link again to continue.';
          return;
        }

        this.localStorageService.set('bearer', token.access_token, token.expires_in);
        this.localStorageService.set('email', email, token.expires_in);
        this.localStorageService.set('login_company_code', companyCode, token.expires_in);
        this.localStorageService.set('mobileNo', token.mobile_number, token.expires_in);
        this.localStorageService.set('enroll_context', 'login', token.expires_in);

        const expiry = Number(this.localStorageService.get('bearer_expiry_actual') || '600000') / 1000;
        this.localStorageService.set('temporary_password', tempPassword, expiry);
        this.router.navigateByUrl('/sign-up/totp-enroll', { skipLocationChange: false });
      },
      error: () => {
        this.isInvalidCode = true;
        this.isError = true;
        this.invalidText = 'Session expired. Please open the invite link again to continue.';
      }
    });
  }

  goToSignIn(): void {
    this.localStorageService.remove('temporary_password');
    this.localStorageService.remove('enroll_context');
    this.router.navigateByUrl('', { skipLocationChange: false });
  }
}
